import { useState } from 'react';
import { createClub, joinClub } from '../../api/clubs';
import type { ClubResponse } from '../../types/api';
import { inputClass } from '../ui/inputClass';

interface Props {
  onClose: () => void;
  onSuccess: (club: ClubResponse) => void;
}

type ActiveTab = 'create' | 'join';

export function CreateJoinModal({ onClose, onSuccess }: Props) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('create');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  function handleTabChange(tab: ActiveTab) {
    setActiveTab(tab);
    setError(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const club = await createClub({ name: createName, description: createDescription || undefined });
      onSuccess(club);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create club');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const club = await joinClub(inviteCode);
      onSuccess(club);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to join club');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative mt-20 w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <h2 className="mb-5 text-lg font-bold text-slate-900 dark:text-slate-50">Clubs</h2>

        <div className="mb-5 flex gap-1 border-b border-slate-200 dark:border-slate-700">
          {(['create', 'join'] as ActiveTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`pb-2.5 px-1 mr-3 text-sm font-medium capitalize transition-colors border-b-2 -mb-px cursor-pointer ${
                activeTab === tab
                  ? 'border-teal-600 dark:border-teal-400 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {tab === 'create' ? 'Create a club' : 'Join with code'}
            </button>
          ))}
        </div>

        {activeTab === 'create' && (
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Club name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                maxLength={100}
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="My fishing crew"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
              <textarea
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="What's this club about? (optional)"
                rows={3}
                className={`${inputClass} resize-none`}
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-teal-600 dark:bg-teal-500 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {submitting ? 'Creating…' : 'Create Club'}
            </button>
          </form>
        )}

        {activeTab === 'join' && (
          <form onSubmit={handleJoin} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Invite code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                className={`${inputClass} font-mono tracking-widest`}
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-teal-600 dark:bg-teal-500 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {submitting ? 'Joining…' : 'Join Club'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
